import { ArbitrumTokenAddress, BaseTokenAddress, MainnetTokenAddress, OptimismTokenAddress } from '@silo/addresses';
import {
  ADDRESS_ZERO,
  ChecksumAddress,
  ProtocolInstance,
  ProtocolInstanceValue,
  getChecksumAddressOrThrow,
} from '@silo/types';
import { flow } from 'effect';
import { PROTOCOL_CONFIG } from '@silo/config';

interface RewardsUiConfig {
  tokenAddress: ChecksumAddress;
  symbol: string;
  decimals: number;
  siloIncentivesControllerAddress: ChecksumAddress;
}

const { mainnetLegacy, mainnetLlama, mainnetGrand, arbitrum, optimism, base, lrtbase } = PROTOCOL_CONFIG;

export const REWARDS_UI_CONFIG: ProtocolInstanceValue<RewardsUiConfig> = {
  mainnetLegacy: {
    tokenAddress: getChecksumAddressOrThrow(mainnetLegacy.nativeRewards.tokenAddress),
    symbol: mainnetLegacy.nativeRewards.symbol,
    decimals: mainnetLegacy.nativeRewards.decimals,
    siloIncentivesControllerAddress: getChecksumAddressOrThrow(
      mainnetLegacy.nativeRewards.siloIncentivesControllerAddress
    ),
  },
  mainnetLlama: {
    tokenAddress: getChecksumAddressOrThrow(mainnetLlama.nativeRewards.tokenAddress),
    symbol: mainnetLlama.nativeRewards.symbol,
    decimals: mainnetLlama.nativeRewards.decimals,
    siloIncentivesControllerAddress: getChecksumAddressOrThrow(
      mainnetLlama.nativeRewards.siloIncentivesControllerAddress
    ),
  },
  mainnetGrand: {
    tokenAddress: getChecksumAddressOrThrow(mainnetGrand.nativeRewards.tokenAddress),
    symbol: mainnetGrand.nativeRewards.symbol,
    decimals: mainnetGrand.nativeRewards.decimals,
    siloIncentivesControllerAddress: getChecksumAddressOrThrow(
      mainnetGrand.nativeRewards.siloIncentivesControllerAddress
    ),
  },
  arbitrum: {
    tokenAddress: getChecksumAddressOrThrow(arbitrum.nativeRewards.tokenAddress),
    symbol: arbitrum.nativeRewards.symbol,
    decimals: arbitrum.nativeRewards.decimals,
    siloIncentivesControllerAddress: getChecksumAddressOrThrow(arbitrum.nativeRewards.siloIncentivesControllerAddress),
  },
  optimism: {
    tokenAddress: getChecksumAddressOrThrow(optimism.nativeRewards.tokenAddress),
    symbol: optimism.nativeRewards.symbol,
    decimals: optimism.nativeRewards.decimals,
    siloIncentivesControllerAddress: getChecksumAddressOrThrow(optimism.nativeRewards.siloIncentivesControllerAddress),
  },
  base: {
    tokenAddress: getChecksumAddressOrThrow(base.nativeRewards.tokenAddress),
    symbol: base.nativeRewards.symbol,
    decimals: base.nativeRewards.decimals,
    siloIncentivesControllerAddress: getChecksumAddressOrThrow(base.nativeRewards.siloIncentivesControllerAddress),
  },
  lrtbase: {
    tokenAddress: getChecksumAddressOrThrow(lrtbase.nativeRewards.tokenAddress),
    symbol: lrtbase.nativeRewards.symbol,
    decimals: lrtbase.nativeRewards.decimals,
    siloIncentivesControllerAddress: getChecksumAddressOrThrow(lrtbase.nativeRewards.siloIncentivesControllerAddress),
  },
  local: {
    tokenAddress: getChecksumAddressOrThrow(ADDRESS_ZERO),
    symbol: 'SILO',
    decimals: 18,
    siloIncentivesControllerAddress: getChecksumAddressOrThrow(ADDRESS_ZERO),
  },
  anvil: {
    tokenAddress: getChecksumAddressOrThrow(ADDRESS_ZERO),
    symbol: 'SILO',
    decimals: 18,
    siloIncentivesControllerAddress: getChecksumAddressOrThrow(ADDRESS_ZERO),
  },
};

const createGetter =
  <K extends keyof RewardsUiConfig>(key: K) =>
  (protocolInstance: ProtocolInstance) =>
    REWARDS_UI_CONFIG[protocolInstance][key];

export const getRewardsTokenSymbol = createGetter('symbol');
export const getRewardsTokenAddress = createGetter('tokenAddress');

const siloGovernanceTokenSet = new Set([
  MainnetTokenAddress.SILO,
  ArbitrumTokenAddress.SILO,
  BaseTokenAddress.SILO,
  OptimismTokenAddress.SILO,
]);

export const getIsSiloGovernanceToken = (tokenAddress: ChecksumAddress) => siloGovernanceTokenSet.has(tokenAddress);

export const getIsSiloRewardsToken = flow(getRewardsTokenAddress, getIsSiloGovernanceToken);

export const getRewardsTokenDecimals = createGetter('decimals');
export const getSiloIncentivesControllerAddress = createGetter('siloIncentivesControllerAddress');
